import { DatePicker } from '@/components/DatePicker';
import LabelWithAsterisk from '@/components/LabelWithAsterisk';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { RootState } from '@/lib/redux/store';
import type { StaticListings } from '@/types/StaticListings';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useSelector } from 'react-redux';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

interface _ListingTypeDetails {
    listingTypes: StaticListings[];
    projectTypes: StaticListings[];
    leaseTerms: StaticListings[];
    forSaleOrRentOptions: StaticListings[];
    rentalTypes: StaticListings[];
    selectedListingType: number | null;
    setSelectedListingType: React.Dispatch<React.SetStateAction<number | null>>;
    selectedProjectType: number | null;
    setSelectedProjectType: React.Dispatch<React.SetStateAction<number | null>>;
    selectedLeaseTerm: number | null;
    setSelectedLeaseTerm: React.Dispatch<React.SetStateAction<number | null>>;
    selectedForSaleOrRent: number | null;
    setSelectedForSaleOrRent: React.Dispatch<React.SetStateAction<number | null>>;
    selectedRentalType: number | null;
    setSelectedRentalType: React.Dispatch<React.SetStateAction<number | null>>;
    selectedAvailableDate?: Date;
    setSelectedAvailableDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
    listingReferenceNumber: string;
    setListingReferenceNumber: React.Dispatch<React.SetStateAction<string>>;
    coAgencyListing: boolean;
    setCoAgencyListing: React.Dispatch<React.SetStateAction<boolean>>;
    coShareListing: boolean;
    setCoShareListing: React.Dispatch<React.SetStateAction<boolean>>;
    forAuction: boolean;
    setForAuction: React.Dispatch<React.SetStateAction<boolean>>;
    forTender: boolean;
    setForTender: React.Dispatch<React.SetStateAction<boolean>>;
    selectedCoShareAgents: {
        id: number,
        firstName: string,
        lastName: string,
        status: number;
        coShareId: number;
    }[];
    setSelectedCoShareAgents: React.Dispatch<React.SetStateAction<{
        id: number,
        firstName: string,
        lastName: string,
        status: number;
        coShareId: number;
    }[]>>;
    selectedCoAgencyAgents: {
        id: number,
        firstName: string,
        lastName: string
    }[];
    setSelectedCoAgencyAgents: React.Dispatch<React.SetStateAction<{
        id: number,
        firstName: string,
        lastName: string
    }[]>>;
    listingId?: number;
    markAsDuplicate?: boolean;
    edit?: boolean;
}

export interface ListingTypeDetailsRef {
    callback: () => boolean;
    getCoAgencyAgents: () => number[];
    getCoShareAgents: () => number[];
}

const ListingTypeDetails = forwardRef<ListingTypeDetailsRef, _ListingTypeDetails>(({
    listingTypes,
    projectTypes,
    leaseTerms,
    rentalTypes,
    forSaleOrRentOptions,
    selectedListingType,
    setSelectedListingType,
    selectedProjectType,
    setSelectedProjectType,
    selectedLeaseTerm,
    setSelectedLeaseTerm,
    selectedForSaleOrRent,
    setSelectedForSaleOrRent,
    selectedRentalType,
    setSelectedRentalType,
    selectedAvailableDate,
    setSelectedAvailableDate,
    listingReferenceNumber,
    setListingReferenceNumber,
    coAgencyListing,
    setCoAgencyListing,
    coShareListing,
    setCoShareListing,
    forAuction,
    setForAuction,
    forTender,
    setForTender,
    selectedCoShareAgents,
    setSelectedCoShareAgents,
    selectedCoAgencyAgents,
    setSelectedCoAgencyAgents,
    listingId,
    markAsDuplicate,
    edit
}, ref) => {
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const agent = useSelector((state: RootState) => state.auth.agent);

    const callback = () => {
        try {
            let valErrors = [];
            if(!selectedListingType) {
                valErrors.push('listingType');
            }

            if(!selectedProjectType) {
                valErrors.push('projectType');
            }

            if(!selectedForSaleOrRent) {
                valErrors.push('forSaleOrRent');
            }

            if(selectedListingType === 1 && selectedForSaleOrRent === 2) {
                if(!selectedRentalType) {
                    valErrors.push('rentalType');
                }
            }

            setValidationErrors(valErrors);
            if (valErrors.length > 0) {
                throw new Error("Please complete the required fields.");
            }
            return true;
        } catch (err : any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            return false
        }
    }

    const getCoAgencyAgents = () => selectedCoAgencyAgents.map(a => a.id);
    const getCoShareAgents = () => selectedCoShareAgents.map(a => a.id);

    const removeError = (field: string) => {
        setValidationErrors(prevErrors => prevErrors.filter(x => x !== field));
    };

    useImperativeHandle(ref, () => ({
        callback,
        getCoAgencyAgents,
        getCoShareAgents
    }));

    useEffect(() => {
        if(selectedAvailableDate) {
            removeError('availableDate');
        }
    }, [selectedAvailableDate]);

    useEffect(() => {
        if (selectedCoAgencyAgents.length > 0) {
            removeError('coAgencyAgents');
        }
    }, [selectedCoAgencyAgents]);

    useEffect(() => {
        if (selectedCoShareAgents.length > 0) {
            removeError('coShareAgents');
        }
    }, [selectedCoShareAgents]);

    useEffect(() => {
        if (!coAgencyListing) {
            setSelectedCoAgencyAgents([]);
            removeError('coAgencyAgents');
        }
    }, [coAgencyListing]);

    useEffect(() => {
        if (!coShareListing) {
            setSelectedCoShareAgents([]);
            removeError('coShareAgents');
        }
    }, [coShareListing]);
    
    return (
        <section>
            <div className="mt-3 pt-5">
                <LabelWithAsterisk 
                label={'Listing Type'} 
                className={'text-black'}/>
                <div className="flex flex-wrap gap-2 mt-3">
                    {
                        listingTypes.map((listing) => (
                            <Button
                            key={listing.value}
                            size={'sm'}
                            className={`cursor-pointer ${selectedListingType !== listing.value && 'text-black'} ${validationErrors.includes('listingType') && 'border-red-400'}`}
                            variant={selectedListingType === listing.value ? 'default' : 'outline'}
                            onClick={(e) => { 
                                e.preventDefault(); 
                                setSelectedListingType(listing.value);
                                removeError('listingType');
                            }}
                            disabled={markAsDuplicate || edit}>
                                { listing.label }
                            </Button>
                        ))
                    }
                </div>
                { validationErrors.includes('listingType') && <span className='text-xs text-red-400'>Listing type is required</span> }
            </div>
            <div className="mt-3 pt-5">
                <LabelWithAsterisk 
                label={'Project type'} 
                className={'text-black'}/>
                <div className="flex flex-wrap gap-2 mt-3">
                {
                    projectTypes?.map((option) => (
                            <Button
                            key={option.value}
                            size="sm"
                            className={`cursor-pointer ${
                                selectedProjectType !== option.value ? 'text-black' : ''
                            } ${
                                validationErrors.includes('projectType') ? 'border-red-400' : ''
                            }`}
                            variant={selectedProjectType === option.value ? 'default' : 'outline'}
                            onClick={(e) => {
                                e.preventDefault();
                                setSelectedProjectType(option.value);
                                removeError('projectType')
                            }}
                            disabled={markAsDuplicate || edit}>
                                {option.label}
                            </Button>
                        ))
                    }
                </div>
                { validationErrors.includes('projectType') && <span className='text-xs text-red-400'>Project type is required.</span>}
            </div>
            <div className="mt-3 pt-5">
                <LabelWithAsterisk 
                label={'For'} 
                className={'text-black'}/>
                <div className="flex flex-wrap gap-2 mt-3">
                {
                    forSaleOrRentOptions?.map((option) => (
                            <Button
                            key={option.value}
                            size="sm"
                            className={`cursor-pointer ${
                                selectedForSaleOrRent !== option.value ? 'text-black' : ''
                            } ${
                                validationErrors.includes('forSaleOrRent') ? 'border-red-400' : ''
                            }`}
                            variant={selectedForSaleOrRent === option.value ? 'default' : 'outline'}
                            onClick={(e) => {
                                e.preventDefault();
                                setSelectedForSaleOrRent(option.value);
                                removeError('forSaleOrRent')
                            }}
                            disabled={markAsDuplicate || edit}>
                                {option.label}
                            </Button>
                        ))
                    }
                </div>
                { validationErrors.includes('forSaleOrRent') && <span className='text-xs text-red-400'>For rent or sale is required.</span>}
            </div>
            {
                selectedForSaleOrRent === 1 && (
                    <>
                        <div className="mt-3 pt-5 flex items-center gap-3">
                            <Switch
                            checked={forAuction}
                            onCheckedChange={(checked) => {
                                setForAuction(checked)
                                if (checked) setForTender(false)
                            }}
                            disabled={markAsDuplicate || edit}
                            />
                            <Label className="text-black">This property will be auctioned</Label>
                        </div>

                        <div className="mt-3 pt-5 flex items-center gap-3">
                            <Switch
                            checked={forTender}
                            onCheckedChange={(checked) => {
                                setForTender(checked)
                                if (checked) setForAuction(false)
                            }}
                            disabled={markAsDuplicate || edit}
                            />
                            <Label className="text-black">This property will be tendered</Label>
                        </div>
                    </>
                )
            }
            {
                selectedListingType === 1 && selectedForSaleOrRent === 2 && (
                    <div className="mt-3 pt-5">
                        <LabelWithAsterisk 
                        label={'Renting out'} 
                        className={'text-black'}/>
                        <div className='mt-3 flex gap-2'>
                            {
                                rentalTypes.map((type) => {
                                    return (
                                        <Button
                                        key={type.value}
                                        size={'sm'}
                                        className={`mx-1 cursor-pointer ${selectedRentalType !== type.value && 'text-black'} ${validationErrors.includes('rentalType') && 'border-red-400'}`}
                                        variant={selectedRentalType === type.value ? 'default' : 'outline'}
                                        onClick={(e) => { 
                                            e.preventDefault(); 
                                            setSelectedRentalType(type.value);
                                            removeError('rentalType');
                                        }}
                                        disabled={markAsDuplicate || edit}>
                                            {type.label}
                                        </Button>
                                    )
                                })
                            }
                        </div>
                        { validationErrors.includes('rentalType') && <span className='text-red-400 text-xs'>Rental type is required.</span>}
                    </div>
                )
            }
            <div className="mt-3 pt-5">
                <Label>Available date</Label>
                <div>
                    <DatePicker 
                    className={`mt-3 ${validationErrors.includes('availableDate') && 'border-red-400'}`}
                    value={selectedAvailableDate ?? undefined}
                    callback={setSelectedAvailableDate}
                    disablePastDates={true}
                    disabled={markAsDuplicate || edit}/>
                </div>
                { validationErrors.includes('availableDate') && <span className='text-red-400 text-xs'>Available date is required.</span>}
            </div>
            <div className="mt-3 pt-5">
                <Label className="text-black">Lease term</Label>
                {
                    leaseTerms.map((term) => (
                        <Button
                        key={term.value}
                        size={'sm'}
                        className={`mx-1 cursor-pointer mt-3 ${selectedLeaseTerm !== term.value && 'text-black'}`}
                        variant={selectedLeaseTerm === term.value ? 'default' : 'outline'}
                        onClick={(e) => { 
                            e.preventDefault(); 
                            setSelectedLeaseTerm(term.value) 
                        }}
                        disabled={markAsDuplicate || edit}>
                            { term.label }
                        </Button>
                    ))
                }
            </div>
            {
                agent.isInternal && (
                    <>
                        <hr className='my-5'/>
                        <div className="py-5 space-y-3">
                            <div className="flex items-center gap-3">
                                <Switch
                                checked={coAgencyListing}
                                onCheckedChange={(checked) => setCoAgencyListing(checked)}
                                disabled={markAsDuplicate || edit}
                                />
                                <Label className="text-black">This is a co-agency listing</Label>
                            </div>
                        </div>
                        <div className="py-5 space-y-3">
                            <div className="flex items-center gap-3">
                                <Switch
                                checked={coShareListing}
                                onCheckedChange={(checked) => setCoShareListing(checked)}
                                disabled={markAsDuplicate || edit}
                                />
                                <Label className="text-black">
                                Do you wish to co-share this listing internally?
                                </Label>
                            </div>

                            <a
                                className="text-sm text-blue-600 underline"
                                target="_blank"
                                rel="noopener noreferrer"
                                href="/assets/docs/CO-SHARE AGREEMENT ON RENTAL OR SALE.pdf"
                            >
                                View Co-Share Agreement
                            </a>

                            <p className="text-sm text-muted-foreground">
                                Please review the Co-Share Agreement carefully. By enabling co-share, you
                                acknowledge and agree to the terms and conditions outlined in this document.
                            </p>
                        </div>
                    </>
                )
            }
            <hr className='my-5'/>
            <div className="py-5">
                <Label className="text-black">Listing reference number</Label>
                <Input className='mt-3' 
                placeholder='eg. 294723'
                value={listingReferenceNumber}
                onChange={(e) => {
                    setListingReferenceNumber(e.target.value);
                }}
                disabled />
            </div>
        </section>
    )
})

export default ListingTypeDetails